import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Load from '../../components/Load';
import { useProfile } from '../../context/ProfileContext';
import {
  LIGA_POR_ID,
  LigaPorIdData,
  LigaPorIdVars,
  atualizarLiga,
  uploadImagemLiga,
  uploadBannerLiga,
  atualizarImagemLiga,
  atualizarBannerLiga,
  LigaInput,
} from '../../services/ligaService';
import { dispatchGlobalError } from '../../context/ApiErrorContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import LeagueOrganizersTab from '../../components/liga/LeagueOrganizersTab';
import LeagueDocumentacaoTab from '../../components/liga/LeagueDocumentacaoTab';
import { PageBreadcrumb } from '../../components/navigation';

const EditLeague: React.FC = () => {
  const { currentProfile, isLoading: profileLoading, setProfile } = useProfile();
  const navigate = useNavigate();
  
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const { data, loading, error, refetch } = useQuery<LigaPorIdData, LigaPorIdVars>(LIGA_POR_ID, {
    variables: { id: ligaId! },
    skip: !ligaId || profileLoading || !currentProfile || currentProfile.tipo !== 'LIGA',
  });

  const liga = data?.ligaPorId;
  const { canEditLiga, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  const [form, setForm] = useState({
    nome: '',
    username: '',
    imagemUrl: '',
    bannerUrl: '',
    enderecoCompleto: '',
    ramoAtividade: '',
  });

  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [selectedBannerFile, setSelectedBannerFile] = useState<File | null>(null);
  const [previewBannerUrl, setPreviewBannerUrl] = useState<string | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [avatarUploaded, setAvatarUploaded] = useState(false);
  const [bannerUploaded, setBannerUploaded] = useState(false);
  const [activeTab, setActiveTab] = useState('informacoes');

  // Preencher formulário quando liga for carregada
  useEffect(() => {
    if (liga) {
      setForm({
        nome: liga.nome || '',
        username: liga.username || '',
        imagemUrl: liga.imagemUrl || '',
        bannerUrl: liga.bannerUrl || '',
        enderecoCompleto: liga.enderecoCompleto || '',
        ramoAtividade: liga.ramoAtividade || '',
      });
      setAvatarUploaded(false);
      setBannerUploaded(false);
    }
  }, [liga]);

  // Cleanup de URLs de preview ao desmontar componente
  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
      }
    };
  }, []);

  // Cleanup de URL antiga quando uma nova for criada
  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
    };
  }, [previewAvatarUrl]);

  useEffect(() => {
    return () => {
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
      }
    };
  }, [previewBannerUrl]);

  // Se ainda está carregando o perfil, mostra loading
  if (profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Se não for perfil de liga, não renderiza nada
  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrorMsg('');
  };

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedAvatarFile(null);
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
        setPreviewAvatarUrl(null);
      }
      return;
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.');
      return;
    }
    setSelectedAvatarFile(file);
    const url = URL.createObjectURL(file);
    setPreviewAvatarUrl(url);
  };

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedBannerFile(null);
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
        setPreviewBannerUrl(null);
      }
      return;
    }
    if (!file.type.startsWith('image/')) {
      dispatchGlobalError('Apenas arquivos de imagem são permitidos.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 5MB.');
      return;
    }
    setSelectedBannerFile(file);
    const url = URL.createObjectURL(file);
    setPreviewBannerUrl(url);
  };

  const handleAvatarUpload = async () => {
    if (!ligaId || !selectedAvatarFile) return;
    setIsUploadingAvatar(true);
    try {
      // Upload file and get URL
      const imageUrl = await uploadImagemLiga(ligaId, selectedAvatarFile);
      
      // Update liga with new image URL
      await atualizarImagemLiga(ligaId, imageUrl);

      // Ressincroniza o perfil ativo para que o avatar no header seja o novo,
      // sem exigir novo login (PRD AC-11).
      if (currentProfile?.tipo === 'LIGA') {
        setProfile({ ...currentProfile, imagemUrl: imageUrl });
      }

      setForm((prev) => ({ ...prev, imagemUrl: imageUrl }));
      setSelectedAvatarFile(null);
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
        setPreviewAvatarUrl(null);
      }
      
      // Refetch to get updated data
      await refetch();
      
      setAvatarUploaded(true);
      setSuccess('Avatar atualizado com sucesso.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Erro já tratado no service
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleBannerUpload = async () => {
    if (!ligaId || !selectedBannerFile) return;
    setIsUploadingBanner(true);
    try {
      // Upload file and get URL
      const bannerUrl = await uploadBannerLiga(ligaId, selectedBannerFile);
      
      // Update liga with new banner URL
      await atualizarBannerLiga(ligaId, bannerUrl);
      
      setForm((prev) => ({ ...prev, bannerUrl: bannerUrl }));
      setSelectedBannerFile(null);
      if (previewBannerUrl) {
        URL.revokeObjectURL(previewBannerUrl);
        setPreviewBannerUrl(null);
      }
      
      // Refetch to get updated data
      await refetch();
      
      setBannerUploaded(true);
      setSuccess('Banner atualizado com sucesso.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Erro já tratado no service
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const validate = (): boolean => {
    if (!form.nome.trim()) {
      setErrorMsg('Nome é obrigatório.');
      return false;
    }
    if (!form.username.trim()) {
      setErrorMsg('Username é obrigatório.');
      return false;
    }
    // Validar formato de username (apenas letras, números, underscore e hífen)
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(form.username)) {
      setErrorMsg('Username pode conter apenas letras, números, underscore (_) e hífen (-).');
      return false;
    }
    setErrorMsg('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ligaId || !canEditLiga) return;
    if (!validate()) return;

    setSaving(true);
    setSuccess('');
    setErrorMsg('');

    try {
      // Construir input sem incluir imagemUrl e bannerUrl se já foram atualizados via upload
      // para evitar atualizações redundantes
      const input: LigaInput = {
        nome: form.nome.trim(),
        username: form.username.trim(),
        enderecoCompleto: form.enderecoCompleto.trim() || null,
        ramoAtividade: form.ramoAtividade.trim() || null,
      };

      // Só incluir imagemUrl e bannerUrl se não foram atualizados via upload
      if (!avatarUploaded) {
        input.imagemUrl = form.imagemUrl || null;
      }
      if (!bannerUploaded) {
        input.bannerUrl = form.bannerUrl || null;
      }

      await atualizarLiga(ligaId, input);
      setSuccess('Liga atualizada com sucesso!');
      
      // Aguardar um pouco antes de redirecionar para mostrar mensagem
      setTimeout(() => {
        navigate('/liga/dashboard');
      }, 1500);
    } catch (err) {
      setErrorMsg('Não foi possível salvar as alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/liga/dashboard');
  };

  if (loading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !liga) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        {error ? 'Erro ao carregar informações da liga.' : 'Liga não encontrada.'}
                      </div>
                      <button className="btn btn-primary mt-3" onClick={handleCancel}>
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Verificar permissão de edição
  if (!canEditLiga) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para editar as informações desta Liga. Apenas administradores podem realizar esta ação.
                        </p>
                      </div>
                      <button className="btn btn-primary mt-3" onClick={handleCancel}>
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const displayAvatarUrl = previewAvatarUrl || form.imagemUrl || '/assets/images/user.png';
  const displayBannerUrl = previewBannerUrl || form.bannerUrl || '/assets/images/comingsoon.svg';

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <PageBreadcrumb
                        items={[
                          { label: 'Dashboard', href: '/liga/dashboard' },
                          { label: 'Editar Liga' },
                        ]}
                        onBack={() => navigate('/liga/dashboard')}
                        variant="bootstrap"
                      />
                    </div>
                    <h3 className="fw-700 mb-4">Editar Liga</h3>

                    {errorMsg && (
                      <div className="alert alert-danger" role="alert">
                        {errorMsg}
                      </div>
                    )}

                    {success && (
                      <div className="alert alert-success" role="alert">
                        {success}
                      </div>
                    )}

                    {/* Tabs Navigation */}
                    <ul className="nav nav-tabs mb-4" role="tablist">
                      <li className="nav-item" role="presentation">
                        <button
                          className={`nav-link ${activeTab === 'informacoes' ? 'active' : ''}`}
                          type="button"
                          onClick={() => setActiveTab('informacoes')}
                          role="tab"
                          aria-selected={activeTab === 'informacoes'}
                        >
                          Informações
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button
                          className={`nav-link ${activeTab === 'organizadores' ? 'active' : ''}`}
                          type="button"
                          onClick={() => setActiveTab('organizadores')}
                          role="tab"
                          aria-selected={activeTab === 'organizadores'}
                          disabled={!canEditLiga}
                        >
                          Organizadores
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button
                          className={`nav-link ${activeTab === 'documentacao' ? 'active' : ''}`}
                          type="button"
                          onClick={() => setActiveTab('documentacao')}
                          role="tab"
                          aria-selected={activeTab === 'documentacao'}
                          disabled={!canEditLiga}
                        >
                          Documentação
                        </button>
                      </li>
                    </ul>

                    {/* Tab Content */}
                    <div className="tab-content">
                      {/* Aba Informações */}
                      <div
                        className={`tab-pane fade ${activeTab === 'informacoes' ? 'show active' : ''}`}
                        role="tabpanel"
                      >
                        <form onSubmit={handleSubmit}>
                      {/* Banner */}
                      <div className="mb-4">
                        <label className="form-label fw-600">Banner</label>
                        <div className="position-relative mb-3">
                          <div
                            className="bg-image-cover bg-image-center rounded-3"
                            style={{
                              height: '200px',
                              backgroundImage: `url("${displayBannerUrl}")`,
                              backgroundColor: '#f0f0f0',
                            }}
                          >
                            {selectedBannerFile && (
                              <div className="position-absolute top-0 end-0 p-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={handleBannerUpload}
                                  disabled={isUploadingBanner || !canEditLiga}
                                  aria-label="Salvar banner da liga"
                                >
                                  {isUploadingBanner ? 'Enviando...' : 'Salvar Banner'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleBannerFileChange}
                          disabled={isUploadingBanner || !canEditLiga}
                        />
                        <small className="text-muted">Tamanho máximo: 5MB. Formatos: JPG, PNG, GIF</small>
                      </div>

                      {/* Avatar */}
                      <div className="mb-4">
                        <label className="form-label fw-600">Avatar</label>
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div
                            className="rounded-circle overflow-hidden"
                            style={{ width: '120px', height: '120px', border: '2px solid #ddd' }}
                          >
                            <img
                              src={displayAvatarUrl}
                              alt="Avatar"
                              className="w-100 h-100"
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                          {selectedAvatarFile && (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleAvatarUpload}
                              disabled={isUploadingAvatar || !canEditLiga}
                              aria-label="Salvar avatar da liga"
                            >
                              {isUploadingAvatar ? 'Enviando...' : 'Salvar Avatar'}
                            </button>
                          )}
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleAvatarFileChange}
                          disabled={isUploadingAvatar || !canEditLiga}
                        />
                        <small className="text-muted">Tamanho máximo: 5MB. Formatos: JPG, PNG, GIF</small>
                      </div>

                      {/* Nome */}
                      <div className="mb-3">
                        <label htmlFor="nome" className="form-label fw-600">
                          Nome <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="nome"
                          name="nome"
                          value={form.nome}
                          onChange={handleChange}
                          required
                          disabled={saving || !canEditLiga}
                        />
                      </div>

                      {/* Username */}
                      <div className="mb-4">
                        <label htmlFor="username" className="form-label fw-600">
                          Username <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="username"
                          name="username"
                          value={form.username}
                          onChange={handleChange}
                          required
                          disabled={saving || !canEditLiga}
                          placeholder="@username"
                        />
                        <small className="text-muted">Pode conter apenas letras, números, underscore (_) e hífen (-)</small>
                      </div>

                      {/* Endereço completo da empresa */}
                      <div className="mb-3">
                        <label htmlFor="enderecoCompleto" className="form-label fw-600">
                          Endereço completo da empresa
                        </label>
                        <textarea
                          className="form-control"
                          id="enderecoCompleto"
                          name="enderecoCompleto"
                          value={form.enderecoCompleto}
                          onChange={handleChange}
                          disabled={saving || !canEditLiga}
                          rows={2}
                          placeholder="Rua, número, complemento, bairro, cidade/UF, CEP"
                        />
                      </div>

                      {/* Ramo / Atividade da empresa */}
                      <div className="mb-4">
                        <label htmlFor="ramoAtividade" className="form-label fw-600">
                          Ramo / Atividade da empresa
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="ramoAtividade"
                          name="ramoAtividade"
                          value={form.ramoAtividade}
                          onChange={handleChange}
                          disabled={saving || !canEditLiga}
                          placeholder="Ex.: Organização de eventos esportivos"
                        />
                      </div>

                      {/* Botões */}
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={saving || isUploadingAvatar || isUploadingBanner || !canEditLiga}
                        >
                          {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </form>
                      </div>

                      {/* Aba Organizadores */}
                      <div
                        className={`tab-pane fade ${activeTab === 'organizadores' ? 'show active' : ''}`}
                        role="tabpanel"
                      >
                        {ligaId && (
                          <LeagueOrganizersTab
                            ligaId={ligaId}
                            canEditLiga={canEditLiga}
                          />
                        )}
                      </div>

                      {/* Aba Documentação */}
                      <div
                        className={`tab-pane fade ${activeTab === 'documentacao' ? 'show active' : ''}`}
                        role="tabpanel"
                      >
                        {ligaId && (
                          <LeagueDocumentacaoTab
                            ligaId={ligaId}
                            canEditLiga={canEditLiga}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditLeague;

